import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { delItem } from '../redux/action/index';
import SignupModal from './buttons/SignupModal';
import useCurrentUser from "../components/common/UseCurrentUser";
import { sendRequest } from '../ApiRequest';
import { sendCachedRequest } from '../utils/apiCache';
import { BASE_URL } from '../URL/constants';
import { v4 as uuidv4 } from 'uuid';
import MessageModal from './Common/MessageModal';
import useMessageModal from '../hooks/useMessageModal';
import { FaTrash, FaMinus, FaPlus, FaShoppingCart, FaExclamationTriangle } from 'react-icons/fa';
import './Cart.css';
import CheckoutHandler from './common/CheckoutHandler';
import { calculateFinalPrice, calculateItemTotal, calculateCartTotal } from '../utils/priceUtils';
import { checkCartStockAvailability } from '../utils/stockUtils';

const defaultImg = "/assets/images/products/p.png";

const Cart = () => {
  const { isLoggedIn, loginUser, currentUser } = useCurrentUser();
  const navigate = useNavigate();
  
  const dispatch = useDispatch();
  const reduxCartItems = useSelector((state) => state.addItem);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);
  const previousItemIdsRef = useRef('');
  const { modalState, showSuccess, showError, showWarning, closeModal } = useMessageModal();
  
  // Force re-check login state after login event (for pet_products business)
  const [loginStateKey, setLoginStateKey] = useState(0);
  const [businessCategory, setBusinessCategory] = useState(null);
  
  // Track if cart sync is in progress to prevent clearing cart during login
  const [isCartSyncInProgress, setIsCartSyncInProgress] = useState(false);
  
  // SignupModal states
  const [activeTab, setActiveTab] = useState("login");
  const [showOtpLogin, setShowOtpLogin] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");



  const getGuestId = () => {
    let guestId = localStorage.getItem("guestId");
    if (!guestId) {
      guestId = uuidv4();
      localStorage.setItem("guestId", guestId);
    }
    return guestId;
  };

  // Helper function to get consistent item ID
  const getItemId = (item) => {
    return item.id || item.item_id || String(item.item_id);
  };

  // Load selection from localStorage on mount and handle new items
  // Only run when item IDs actually change (items added/removed), not when quantities change
  useEffect(() => {
    // Get current item IDs as a sorted string
    const currentItemIds = cartItems.map(item => getItemId(item)).sort().join(',');
    
    // Skip if item IDs haven't changed (e.g., only quantity changed)
    if (currentItemIds === previousItemIdsRef.current && hasInitializedSelection) {
      return;
    }
    
    // Update ref with current item IDs
    previousItemIdsRef.current = currentItemIds;
    
    if (cartItems.length === 0) {
      // Clear selection if cart is empty
      setSelectedItems(new Set());
      localStorage.removeItem('cartSelectedItems');
      setHasInitializedSelection(false);
      return;
    }

    if (!hasInitializedSelection) {
      // Initial load - try to restore from localStorage
      try {
        const savedSelection = localStorage.getItem('cartSelectedItems');
        if (savedSelection) {
          const savedIds = JSON.parse(savedSelection);
          const validIds = new Set(
            savedIds.filter(id => 
              cartItems.some(item => getItemId(item) === id)
            )
          );
          // Auto-select any new items that weren't in saved selection
          const allItemIds = new Set(cartItems.map(item => getItemId(item)));
          allItemIds.forEach(id => {
            if (!validIds.has(id)) {
              validIds.add(id); // Auto-select new items
            }
          });
          setSelectedItems(validIds);
          setHasInitializedSelection(true);
          return;
        }
      } catch (error) {
        console.error('Error loading saved selection:', error);
      }
      
      // If no saved selection, auto-select all items on initial load only
      const allItemIds = new Set(cartItems.map(item => getItemId(item)));
      setSelectedItems(allItemIds);
      setHasInitializedSelection(true);
    } else {
      // After initialization, only add new items to selection (preserve user's deselections)
      setSelectedItems(prevSelected => {
        const newSelection = new Set(prevSelected);
        const allItemIds = new Set(cartItems.map(item => getItemId(item)));
        
        // Remove items that are no longer in cart
        prevSelected.forEach(id => {
          if (!allItemIds.has(id)) {
            newSelection.delete(id);
          }
        });
        
        // Auto-select new items that weren't previously selected
        allItemIds.forEach(id => {
          if (!prevSelected.has(id)) {
            newSelection.add(id); // Auto-select new items
          }
        });
        
        return newSelection;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems, hasInitializedSelection]);

  // Save selection to localStorage whenever it changes
  useEffect(() => {
    if (hasInitializedSelection) {
      try {
        const selectedArray = Array.from(selectedItems);
        localStorage.setItem('cartSelectedItems', JSON.stringify(selectedArray));
      } catch (error) {
        console.error('Error saving selection:', error);
      }
    }
  }, [selectedItems, hasInitializedSelection]);

  // Fetch business category to check if it's pet_products
  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await sendCachedRequest(
          'POST',
          `${BASE_URL}/v1/public_site/public_site_info/get_list`,
          null,
          { cacheKey: 'public_site_info_cart' }
        );
        
        const apiBusinessCategory = response?.data?.data?.business_category;
        setBusinessCategory(apiBusinessCategory);
      } catch (error) {
        console.error('Error fetching business info in Cart:', error);
      }
    };

    fetchBusinessInfo();
  }, []);

  // Listen for user-logged-in event to force login state update (especially for pet_products)
  useEffect(() => {
    const handleUserLoggedIn = () => {
      // Force re-check of login state by updating a key
      setLoginStateKey(prev => prev + 1);
      
      // Small delay to ensure localStorage is updated
      setTimeout(() => {
        // Re-check login state from localStorage
        const userDetails = JSON.parse(localStorage.getItem("userDetails"));
        if (userDetails && userDetails.data?.customer?.id) {
          // Force component to recognize user is logged in
          // The useCurrentUser hook should update, but we force a re-check here
          console.log('User logged in - forcing state update');
        }
      }, 100);
    };

    window.addEventListener('user-logged-in', handleUserLoggedIn);
    
    return () => {
      window.removeEventListener('user-logged-in', handleUserLoggedIn);
    };
  }, []);

  useEffect(() => {
    // Don't refetch cart items if checkout is in progress or cart sync is in progress
    if (isProcessingCheckout || isCartSyncInProgress) {
      return;
    }
    
    const fetchCartItems = async () => {
      setLoading(true);
              if (!isLoggedIn || !currentUser?.data?.customer) {
          const guestCart = JSON.parse(localStorage.getItem("guestCart")) || {};
          const guestCartItems = Object.values(guestCart);
          
          // Combine guest cart from localStorage with Redux state for immediate updates
          // Use item_id (product ID) for deduplication, not cart item ID
          const seenItemIds = new Set();
          const uniqueItems = [];
          
          // First, add items from localStorage
          guestCartItems.forEach(item => {
            const productId = item.item_id || item.id;
            if (productId && !seenItemIds.has(productId)) {
              seenItemIds.add(productId);
              uniqueItems.push(item);
            }
          });
          
          // Then, add items from Redux that aren't already in the list
          reduxCartItems.forEach(item => {
            const productId = item.item_id || item.id;
            if (productId && !seenItemIds.has(productId)) {
              seenItemIds.add(productId);
              uniqueItems.push(item);
            }
          });
        
        // Get discount info and image for each item
        const updatedItems = await Promise.all(
          uniqueItems.map(async (item) => {
            try {
              const productId = item.item_id || item.id;
              const response = await sendRequest('GET', `${BASE_URL}/v1/public_site/menus/item_detail?id=${productId}`);
              const itemDetails = response.data.data;
              // Get image from item_url, item_images array, or img property
              const itemImage = itemDetails.item_url || 
                               (itemDetails.item_images && itemDetails.item_images[0]) || 
                               itemDetails.img || 
                               item.img;
              return { 
                ...item, 
                img: itemImage,
                sales_discount: itemDetails.sales_discount || 0 
              };
            } catch (error) {
              return { ...item, sales_discount: 0 };
            }
          })
        );
        
        // Remove duplicates based on item_id before setting state
        const deduplicatedItems = [];
        const seenProductIds = new Set();
        updatedItems.forEach(item => {
          const productId = item.item_id || item.id;
          if (productId && !seenProductIds.has(productId)) {
            seenProductIds.add(productId);
            deduplicatedItems.push(item);
          }
        });
        
        setCartItems(deduplicatedItems);
        
        // Update localStorage with deduplicated items only
        const updatedGuestCart = {};
        deduplicatedItems.forEach(item => {
          const productId = item.item_id || item.id;
          if (productId) {
            // Use productId as key to prevent duplicates
            const cartItemId = item.id || `guest_${productId}`;
            updatedGuestCart[cartItemId] = {
              ...item,
              id: cartItemId,
              item_id: productId
            };
          }
        });
        
        // Replace localStorage instead of merging to avoid duplicates
        localStorage.setItem("guestCart", JSON.stringify(updatedGuestCart));
        
        setLoading(false);
        return;
      }

        try {
          const response = await sendRequest(
            'GET',
            `${BASE_URL}/v1/public_site/customers/${currentUser.data.customer.id}/cart`,
            null,
            { Authorization: currentUser.data.token }
          );
          const apiCartItems = response.data.cart_items || [];
        
        // Only update cart if we have items from API, otherwise preserve existing cart items
        // This prevents clearing cart during sync or when API returns empty cart temporarily
        if (apiCartItems.length > 0) {
          // Fetch item details for each cart item to get stock information and discount
          const itemsWithStock = await Promise.all(
            apiCartItems.map(async (cartItem) => {
              try {
                const itemResponse = await sendRequest(
                  'GET',
                  `${BASE_URL}/v1/public_site/menus/item_detail?id=${cartItem.item_id}`,
                  null,
                  { Authorization: currentUser.data.token }
                );
                const itemDetails = itemResponse.data.data;
                // Get image from item_url, item_images array, or img property
                const itemImage = itemDetails.item_url || 
                                 (itemDetails.item_images && itemDetails.item_images[0]) || 
                                 itemDetails.img || 
                                 cartItem.img;
                return {
                  ...cartItem,
                  available_quantity: itemDetails.stock_quantity || itemDetails.available_quantity || 0,
                  unit_name: itemDetails.unit_name || 'units',
                  img: itemImage,
                  sales_discount: itemDetails.sales_discount || 0
                };
              } catch (error) {
                return {
                  ...cartItem,
                  available_quantity: 0,
                  unit_name: 'units',
                  sales_discount: 0
                };
              }
            })
          );
          
          // For logged-in users, use ONLY API cart items (source of truth)
          // Don't combine with Redux to avoid duplicates
          // The API cart is the authoritative source and should already contain all items
          setCartItems(itemsWithStock);
        } else if (cartItems.length === 0) {
          // Only set empty cart if we don't have any items in state
          // This preserves cart items during sync
          setCartItems([]);
        }
        // If API returns empty but we have items in state, keep the state items
      } catch (error) {
        console.error('Error fetching cart items:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCartItems();
  }, [isLoggedIn, currentUser, reduxCartItems, isProcessingCheckout, loginStateKey, isCartSyncInProgress]);

  const handleQuantityChange = async (cartItem, change) => {
    const newQuantity = cartItem.quantity + change;
    
    // If quantity would be 0 or less, remove the item from cart
    if (newQuantity < 1) {
      await handleRemoveItem(cartItem);
      return;
    }

    // Check if trying to increase beyond available stock (skip validation for unlimited items with -1)
    if (change > 0 && cartItem.available_quantity && cartItem.available_quantity !== -1 && newQuantity > cartItem.available_quantity) {
      showWarning(`Only ${cartItem.available_quantity} items available in stock.`);
      return;
    }

    if (!isLoggedIn) {
      // Handle guest cart quantity change
      const guestCart = JSON.parse(localStorage.getItem("guestCart")) || {};
      const updatedCart = Object.values(guestCart).map(item => 
        item.id === cartItem.id ? { ...item, quantity: newQuantity } : item
      );
      localStorage.setItem("guestCart", JSON.stringify(updatedCart));
      setCartItems(updatedCart);
      // Dispatch cart-updated event to update cart count immediately
      window.dispatchEvent(new Event('cart-updated'));
      return;
    }

    try {
      await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/customers/${currentUser.data.customer.id}/cart/cart_items`,
        { item_id: cartItem.item_id, quantity: change },
        { Authorization: currentUser.data.token }
      );
      setCartItems(prevItems =>
        prevItems.map(item =>
          item.id === cartItem.id ? { ...item, quantity: newQuantity } : item
        )
      );
      // Dispatch cart-updated event to update cart count immediately
      window.dispatchEvent(new Event('cart-updated'));
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  };

  const handleRemoveItem = async (item) => {
    // Remove from selected items if present
    const itemId = getItemId(item);
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });

    if (!isLoggedIn) {
      const guestCartObj = JSON.parse(localStorage.getItem("guestCart")) || {};
      const guestCartArray = Object.values(guestCartObj);
      const updatedCart = guestCartArray.filter((cartItem) => {
        const cartItemId = cartItem.item_id || cartItem.id;
        const itemIdToRemove = item.item_id || item.id;
        return cartItemId !== itemIdToRemove;
      });
      
      // Convert back to object format
      const updatedGuestCart = {};
      updatedCart.forEach(cartItem => {
        const cartItemId = cartItem.id || `guest_${Date.now()}_${cartItem.item_id || cartItem.id}`;
        updatedGuestCart[cartItemId] = cartItem;
      });
      
      localStorage.setItem("guestCart", JSON.stringify(updatedGuestCart));
      setCartItems(updatedCart);
      dispatch(delItem(item));
      // Dispatch cart-updated event to update cart count immediately
      window.dispatchEvent(new Event('cart-updated'));
      return;
    }

    try {
      // Use item_id for deletion (backend accepts item_id)
      const itemIdToDelete = item.item_id || item.id;
      
      if (!itemIdToDelete) {
        console.error('Cannot delete item: missing item_id or id', item);
        showError('Cannot remove item: missing item information.');
        return;
      }

      await sendRequest(
        'DELETE',
        `${BASE_URL}/v1/public_site/customers/${currentUser.data.customer.id}/cart/cart_items/${itemIdToDelete}`,
        null,
        { Authorization: currentUser.data.token }
      );
      
      // Remove from local state
      setCartItems(prevItems => 
        prevItems.filter(cartItem => {
          const cartItemId = cartItem.item_id || cartItem.id;
          return cartItemId !== itemIdToDelete;
        })
      );
      
      dispatch(delItem(item));
      
      // Update cart state
      const cartState = JSON.parse(localStorage.getItem("cartState")) || {};
      delete cartState[itemIdToDelete];
      localStorage.setItem("cartState", JSON.stringify(cartState));
      
      // Dispatch cart-updated event to update cart count immediately
      window.dispatchEvent(new Event('cart-updated'));
    } catch (error) {
      console.error('Error removing item:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to remove item from cart.';
      showError(errorMessage);
      
      // Even if API fails, remove from local state to keep UI in sync
      const itemIdToDelete = item.item_id || item.id;
      if (itemIdToDelete) {
        setCartItems(prevItems => 
          prevItems.filter(cartItem => {
            const cartItemId = cartItem.item_id || cartItem.id;
            return cartItemId !== itemIdToDelete;
          })
        );
        dispatch(delItem(item));
        const cartState = JSON.parse(localStorage.getItem("cartState")) || {};
        delete cartState[itemIdToDelete];
        localStorage.setItem("cartState", JSON.stringify(cartState));
        window.dispatchEvent(new Event('cart-updated'));
      }
    }
  };

  const handleToggleItemSelection = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allItemIds = new Set(cartItems.map(item => getItemId(item)));
    setSelectedItems(allItemIds);
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
  };



  const fetchCustomerData = async (customerId) => {
    try {
      const response = await sendRequest(
        'GET',
        `${BASE_URL}/v1/public_site/customers/${customerId}`,
        null,
        { Authorization: currentUser.data.token }
      );
      setCustomerData(response.data);
    } catch (error) {
      console.error('Error fetching customer data:', error);
      setError(error.message);
    }
  };

  // Effect to control SignupModal
  useEffect(() => {
    const modal = document.getElementById('signupModal');
    if (!modal || typeof window === 'undefined' || !window.bootstrap) {
      return;
    }

    if (showLoginModal) {
      // Check if an instance already exists
      let bootstrapModal = window.bootstrap.Modal.getInstance(modal);
      
      // If instance exists, dispose it first to avoid conflicts
      if (bootstrapModal) {
        bootstrapModal.dispose();
      }
      
      // Create a new modal instance
      bootstrapModal = new window.bootstrap.Modal(modal, {
        backdrop: true,
        keyboard: true
      });
      
      // Show the modal
      bootstrapModal.show();
      
      // Listen for hidden event to reset state
      const handleHidden = () => {
        setShowLoginModal(false);
        modal.removeEventListener('hidden.bs.modal', handleHidden);
      };
      
      modal.addEventListener('hidden.bs.modal', handleHidden);
      
      // Cleanup
      return () => {
        modal.removeEventListener('hidden.bs.modal', handleHidden);
        const instance = window.bootstrap.Modal.getInstance(modal);
        if (instance) {
          instance.hide();
        }
      };
    } else {
      // If showLoginModal is false, make sure modal is hidden
      const instance = window.bootstrap.Modal.getInstance(modal);
      if (instance) {
        instance.hide();
      }
    }
  }, [showLoginModal]);

  const closeSignupModal = () => {
    // Close Bootstrap modal first
    const modal = document.getElementById('signupModal');
    if (modal && typeof window !== 'undefined' && window.bootstrap) {
      const bootstrapModal = window.bootstrap.Modal.getInstance(modal);
      if (bootstrapModal) {
        bootstrapModal.hide();
      }
    }
    
    // Reset state after a small delay to ensure modal is closed
    setTimeout(() => {
      setShowLoginModal(false);
      setShowOtpLogin(false);
      setPhoneNumber("");
      setOtp("");
      setFirstName("");
      setLastName("");
      setEmail("");
      setActiveTab("login"); // Reset to login tab
    }, 100);
  };

  const handleSendOtp = async () => {
    if (!phoneNumber || phoneNumber.length !== 10) {
      console.log("Please enter a valid 10-digit phone number");
      return;
    }

    try {
      const response = await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/customers/send_otp`,
        { customer: { login: phoneNumber } }
      );

      if (response.data) {
        setShowOtpLogin(true);
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      console.log("Please enter a valid 6-digit OTP");
      return;
    }

    // CRITICAL: Preserve guest cart IMMEDIATELY at the start of login process
    // This prevents it from being cleared by any other operations
    const preservedGuestCart = localStorage.getItem("guestCart");
    console.log("PRESERVING guest cart at login start:", preservedGuestCart);
    
    // Preserve current cart items in state to prevent them from being cleared
    const currentCartItems = [...cartItems];
    setIsCartSyncInProgress(true);

    try {
      const response = await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/customers/verify_otp`,
        { customer: { login: phoneNumber, otp: otp } }
      );

      if (response.data) {
        if (response.data.message === "Login successful") {
          if (!response.data.token) {
            console.error("No token in response:", response.data);
            setIsCartSyncInProgress(false);
            return;
          }
          
          if (!response.data.customer || !response.data.customer.id) {
            console.error("No customer data in response:", response.data);
            setIsCartSyncInProgress(false);
            return;
          }
          
          localStorage.setItem("authToken", response.data.token);
          
          // Prepare user data structure that matches what loginUser expects
          const userData = {
            data: {
              customer: response.data.customer,
              token: response.data.token
            }
          };
          
          // Store userDetails with the correct structure
          localStorage.setItem("userDetails", JSON.stringify(userData));
          loginUser(userData);
          
          // Verify login was successful
          const storedUser = JSON.parse(localStorage.getItem("userDetails"));
          if (!storedUser || !storedUser.data || !storedUser.data.customer) {
            console.error("Failed to store user data properly");
            return;
          }
          
          // Dispatch event to refresh header after login
          window.dispatchEvent(new Event('user-logged-in'));
          
          // CRITICAL: Keep current cart items visible during sync to prevent clearing
          // This ensures cart items remain visible even when useEffect tries to fetch
          // We preserve the cart items in state until sync completes
          if (currentCartItems.length > 0) {
            setCartItems(currentCartItems);
            console.log("Preserved cart items in state:", currentCartItems.length);
          }
          
          // Sync guest cart if exists - use PRESERVED cart from start of function
          // This ensures we have the cart even if something cleared localStorage
          let guestCart = {};
          try {
            // First try preserved cart, then fallback to localStorage
            const guestCartRaw = preservedGuestCart || localStorage.getItem("guestCart");
            console.log("Using preserved guest cart:", guestCartRaw);
            guestCart = guestCartRaw ? JSON.parse(guestCartRaw) : {};
          } catch (e) {
            console.error("Error parsing guest cart:", e);
            guestCart = {};
          }
          
          const guestCartItems = Array.isArray(guestCart) ? guestCart : Object.values(guestCart);
          
          console.log("Guest cart items before sync:", guestCartItems);
          console.log("Guest cart object:", guestCart);
          console.log("Guest cart items length:", guestCartItems.length);
          console.log("Customer ID:", response.data.customer?.id);
          
          if (guestCartItems.length > 0 && response.data.customer?.id) {
            // Filter out invalid items and map to sync format
            // Note: item.item_id is the actual product ID, but if not present, use item.id
            const itemsToSync = guestCartItems
              .filter(item => {
                // Use item_id if present, otherwise use id (some guest carts use id as product ID)
                const itemId = item.item_id || item.id;
                const parsedId = parseInt(itemId);
                return itemId && !isNaN(parsedId) && parsedId > 0;
              })
              .map(item => {
                // Use item_id if present, otherwise use id
                const itemId = parseInt(item.item_id || item.id);
                const quantity = parseInt(item.quantity || 1);
                return {
                  item_id: itemId,
                  quantity: quantity > 0 ? quantity : 1
                };
              });

            console.log("Items to sync:", itemsToSync);

            if (itemsToSync.length > 0) {
              try {
                // IMPORTANT: DO NOT clear existing cart items
                // The backend sync_guest_cart endpoint already handles merging:
                // - If item exists, it adds quantities
                // - If item doesn't exist, it creates new
                // This preserves existing cart items and merges guest cart with them
                console.log("Sending sync request with:", {
                  url: `${BASE_URL}/v1/public_site/customers/${response.data.customer.id}/cart/sync_guest_cart`,
                  items: itemsToSync,
                  token: response.data.token ? "Present" : "Missing"
                });
                
                // Wait for cart sync to complete
                // Note: sendRequest expects Authorization in options, not options.headers
                const syncResponse = await sendRequest(
                  "POST",
                  `${BASE_URL}/v1/public_site/customers/${response.data.customer.id}/cart/sync_guest_cart`,
                  { items: itemsToSync },
                  { 
                    Authorization: response.data.token
                  }
                );
                
                console.log("Cart sync response:", syncResponse);
                console.log("Sync response status:", syncResponse?.status);
                console.log("Sync response data:", syncResponse?.data);
                
                // Wait a moment for server to process
                await new Promise(resolve => setTimeout(resolve, 300));
                
                // Verify cart was synced by fetching it
                const verifyResponse = await sendRequest(
                  'GET',
                  `${BASE_URL}/v1/public_site/customers/${response.data.customer.id}/cart`,
                  null,
                  { 
                    Authorization: response.data.token
                  }
                );
                
                console.log("Cart after sync verification:", verifyResponse.data);
                console.log("Cart items count:", verifyResponse.data?.cart_items?.length || 0);
                
                // Only clear guest cart if sync was successful
                if (verifyResponse.data?.cart_items?.length > 0) {
                  localStorage.removeItem("guestCart");
                  console.log("Guest cart cleared after successful sync");
                } else if (syncResponse?.status === 200 || syncResponse?.status === 201) {
                  // Even if verification shows empty, if API returned success, clear guest cart
                  // This handles race conditions where cart might not be immediately available
                  localStorage.removeItem("guestCart");
                  console.log("Guest cart cleared - API returned success");
                } else {
                  console.warn("Cart sync may have failed - keeping guest cart");
                  console.warn("Sync status:", syncResponse?.status);
                  console.warn("Verification cart items:", verifyResponse.data?.cart_items);
                }
                
                // Dispatch cart-updated event to refresh all components
                window.dispatchEvent(new Event('cart-updated'));
                
                // Wait a bit more to ensure cart is fully synced on server
                await new Promise(resolve => setTimeout(resolve, 500));
                
                // Update cart items from verified response to show merged cart
                if (verifyResponse.data?.cart_items?.length > 0) {
                  // Fetch full item details for merged cart
                  const mergedItemsWithDetails = await Promise.all(
                    verifyResponse.data.cart_items.map(async (cartItem) => {
                      try {
                        const itemResponse = await sendRequest(
                          'GET',
                          `${BASE_URL}/v1/public_site/menus/item_detail?id=${cartItem.item_id}`,
                          null,
                          { Authorization: response.data.token }
                        );
                        const itemDetails = itemResponse.data.data;
                        const itemImage = itemDetails.item_url || 
                                         (itemDetails.item_images && itemDetails.item_images[0]) || 
                                         itemDetails.img || 
                                         cartItem.img;
                        return {
                          ...cartItem,
                          available_quantity: itemDetails.stock_quantity || itemDetails.available_quantity || 0,
                          unit_name: itemDetails.unit_name || 'units',
                          img: itemImage,
                          sales_discount: itemDetails.sales_discount || 0
                        };
                      } catch (error) {
                        return {
                          ...cartItem,
                          available_quantity: 0,
                          unit_name: 'units',
                          sales_discount: 0
                        };
                      }
                    })
                  );
                  setCartItems(mergedItemsWithDetails);
                  console.log("Updated cart with merged items:", mergedItemsWithDetails.length);
                }
                
                // Mark sync as complete so useEffect can fetch updated cart
                setIsCartSyncInProgress(false);
              } catch (syncError) {
                // sendRequest throws error.response || error.message
                // So syncError might be the response object directly
                const errorResponse = syncError?.response || syncError;
                const errorData = errorResponse?.data || syncError?.data || syncError;
                const errorStatus = errorResponse?.status || syncError?.status;
                
                console.error("Error syncing guest cart:", syncError);
                console.error("Sync error response:", errorResponse);
                console.error("Sync error data:", errorData);
                console.error("Sync error status:", errorStatus);
                console.error("Full error object:", syncError);
                
                // If it's a 401, the token might be invalid - try using authToken from localStorage
                if (errorStatus === 401) {
                  console.warn("401 Unauthorized - token might be invalid, trying with localStorage token");
                  const storedToken = localStorage.getItem("authToken");
                  if (storedToken && storedToken !== response.data.token) {
                    console.log("Retrying with stored token");
                    try {
                      const retryResponse = await sendRequest(
                        "POST",
                        `${BASE_URL}/v1/public_site/customers/${response.data.customer.id}/cart/sync_guest_cart`,
                        { items: itemsToSync },
                        { Authorization: storedToken }
                      );
                      console.log("Retry successful:", retryResponse);
                      localStorage.removeItem("guestCart");
                      window.dispatchEvent(new Event('cart-updated'));
                    } catch (retryError) {
                      console.error("Retry also failed:", retryError);
                    }
                  }
                }
                
                // Don't clear guest cart if sync fails
                // User can retry or items will remain in guest cart
                // Mark sync as complete even on error so cart can be fetched
                setIsCartSyncInProgress(false);
              }
            } else {
              console.warn("No valid items to sync from guest cart");
              console.warn("Guest cart items were:", guestCartItems);
              // Mark sync as complete
              setIsCartSyncInProgress(false);
            }
          } else {
            console.log("No guest cart items or customer ID missing");
            // Mark sync as complete
            setIsCartSyncInProgress(false);
          }
          
          // Close modal properly
          const modalElement = document.getElementById("signupModal");
          if (modalElement && window.bootstrap) {
            const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
            if (modalInstance) {
              modalInstance.hide();
            }
          }
          closeSignupModal();
          
          // Force update login state immediately for pet_products business
          // This ensures checkout button works right after login
          if (businessCategory === "pet_products") {
            // Force re-check login state
            setLoginStateKey(prev => prev + 1);
            // Wait a bit for state to update, then proceed
            setTimeout(() => {
              // If on cart page, don't navigate - let user proceed with checkout
              // Otherwise navigate to customer-info
              if (window.location.pathname === '/cart') {
                // Stay on cart page, user can click checkout again
                console.log('User logged in on cart page - ready for checkout');
              } else {
                navigate('/customer-info');
              }
            }, 500);
          } else {
            // After successful login and cart sync, navigate to customer-info
            // Give enough time for cart to be fully synced
            setTimeout(() => {
              navigate('/customer-info');
            }, 800);
          }
        } else {
          // New user, show signup fields
          setActiveTab("signup");
          // Reset sync flag if login didn't complete
          setIsCartSyncInProgress(false);
        }
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      // Reset sync flag on error
      setIsCartSyncInProgress(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    
    if (!firstName || !lastName || !email) {
      console.log("Please fill in all required fields");
      return;
    }

    // CRITICAL: Preserve guest cart IMMEDIATELY at the start of signup process
    // This prevents it from being cleared by any other operations
    const preservedGuestCart = localStorage.getItem("guestCart");
    console.log("PRESERVING guest cart at signup start:", preservedGuestCart);
    
    // Preserve current cart items in state to prevent them from being cleared
    const currentCartItems = [...cartItems];
    setIsCartSyncInProgress(true);

    try {
      const response = await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/customers/sign_up`,
        {
          customer: {
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone: phoneNumber,
          }
        }
      );

      if (response.data) {
        // Store authToken separately
        if (response.data.token) {
          localStorage.setItem("authToken", response.data.token);
        }
        
        // Prepare user data structure that matches what loginUser expects
        const userData = {
          data: {
            customer: response.data.customer,
            token: response.data.token
          }
        };
        
        // Store userDetails with the correct structure
        localStorage.setItem("userDetails", JSON.stringify(userData));
        loginUser(userData);
        
        // Dispatch event to refresh header after signup
        window.dispatchEvent(new Event('user-logged-in'));
        
        // CRITICAL: Keep current cart items visible during sync to prevent clearing
        // This ensures cart items remain visible even when useEffect tries to fetch
        // We preserve the cart items in state until sync completes
        if (currentCartItems.length > 0) {
          setCartItems(currentCartItems);
          console.log("Preserved cart items in state (signup):", currentCartItems.length);
        }
        
        // Sync guest cart if exists - use PRESERVED cart from start of function
        // This ensures we have the cart even if something cleared localStorage
        let guestCart = {};
        try {
          // First try preserved cart, then fallback to localStorage
          const guestCartRaw = preservedGuestCart || localStorage.getItem("guestCart");
          console.log("Using preserved guest cart (signup):", guestCartRaw);
          guestCart = guestCartRaw ? JSON.parse(guestCartRaw) : {};
        } catch (e) {
          console.error("Error parsing guest cart (signup):", e);
          guestCart = {};
        }
        
        const guestCartItems = Array.isArray(guestCart) ? guestCart : Object.values(guestCart);
        
        console.log("Guest cart items before sync (signup):", guestCartItems);
        console.log("Guest cart object (signup):", guestCart);
        console.log("Guest cart items length (signup):", guestCartItems.length);
        console.log("Customer ID (signup):", response.data.customer?.id);
        
        if (guestCartItems.length > 0 && response.data.customer?.id && response.data.token) {
          // Filter out invalid items and map to sync format
          // Note: item.item_id is the actual product ID, but if not present, use item.id
          const itemsToSync = guestCartItems
            .filter(item => {
              // Use item_id if present, otherwise use id (some guest carts use id as product ID)
              const itemId = item.item_id || item.id;
              const parsedId = parseInt(itemId);
              return itemId && !isNaN(parsedId) && parsedId > 0;
            })
            .map(item => {
              // Use item_id if present, otherwise use id
              const itemId = parseInt(item.item_id || item.id);
              const quantity = parseInt(item.quantity || 1);
              return {
                item_id: itemId,
                quantity: quantity > 0 ? quantity : 1
              };
            });

          console.log("Items to sync (signup):", itemsToSync);

          if (itemsToSync.length > 0) {
            try {
              // IMPORTANT: DO NOT clear existing cart items
              // The backend sync_guest_cart endpoint already handles merging:
              // - If item exists, it adds quantities
              // - If item doesn't exist, it creates new
              // This preserves existing cart items and merges guest cart with them
              console.log("Sending sync request (signup) with:", {
                url: `${BASE_URL}/v1/public_site/customers/${response.data.customer.id}/cart/sync_guest_cart`,
                items: itemsToSync,
                token: response.data.token ? "Present" : "Missing"
              });
              
              // Wait for cart sync to complete
              // Note: sendRequest expects Authorization in options, not options.headers
              const syncResponse = await sendRequest(
                "POST",
                `${BASE_URL}/v1/public_site/customers/${response.data.customer.id}/cart/sync_guest_cart`,
                { items: itemsToSync },
                { 
                  Authorization: response.data.token
                }
              );
              
              console.log("Cart sync response (signup):", syncResponse);
              console.log("Sync response status (signup):", syncResponse?.status);
              console.log("Sync response data (signup):", syncResponse?.data);
              
              // Wait a moment for server to process
              await new Promise(resolve => setTimeout(resolve, 300));
              
              // Verify cart was synced by fetching it
              const verifyResponse = await sendRequest(
                'GET',
                `${BASE_URL}/v1/public_site/customers/${response.data.customer.id}/cart`,
                null,
                { 
                  Authorization: response.data.token
                }
              );
              
              console.log("Cart after sync (signup):", verifyResponse.data);
              console.log("Cart items count (signup):", verifyResponse.data?.cart_items?.length || 0);
              
              // Only clear guest cart if sync was successful
              if (verifyResponse.data?.cart_items?.length > 0) {
                localStorage.removeItem("guestCart");
                console.log("Guest cart cleared after successful sync (signup)");
              } else if (syncResponse?.status === 200 || syncResponse?.status === 201) {
                localStorage.removeItem("guestCart");
                console.log("Guest cart cleared - API returned success (signup)");
              } else {
                console.warn("Cart sync may have failed - keeping guest cart (signup)");
                console.warn("Sync status (signup):", syncResponse?.status);
                console.warn("Verification cart items (signup):", verifyResponse.data?.cart_items);
              }
              
              // Dispatch cart-updated event to refresh all components
              window.dispatchEvent(new Event('cart-updated'));
              
              // Wait a bit more to ensure cart is fully synced on server
              await new Promise(resolve => setTimeout(resolve, 500));
              
              // Update cart items from verified response to show merged cart
              if (verifyResponse.data?.cart_items?.length > 0) {
                // Fetch full item details for merged cart
                const mergedItemsWithDetails = await Promise.all(
                  verifyResponse.data.cart_items.map(async (cartItem) => {
                    try {
                      const itemResponse = await sendRequest(
                        'GET',
                        `${BASE_URL}/v1/public_site/menus/item_detail?id=${cartItem.item_id}`,
                        null,
                        { Authorization: response.data.token }
                      );
                      const itemDetails = itemResponse.data.data;
                      const itemImage = itemDetails.item_url || 
                                       (itemDetails.item_images && itemDetails.item_images[0]) || 
                                       itemDetails.img || 
                                       cartItem.img;
                      return {
                        ...cartItem,
                        available_quantity: itemDetails.stock_quantity || itemDetails.available_quantity || 0,
                        unit_name: itemDetails.unit_name || 'units',
                        img: itemImage,
                        sales_discount: itemDetails.sales_discount || 0
                      };
                    } catch (error) {
                      return {
                        ...cartItem,
                        available_quantity: 0,
                        unit_name: 'units',
                        sales_discount: 0
                      };
                    }
                  })
                );
                setCartItems(mergedItemsWithDetails);
                console.log("Updated cart with merged items (signup):", mergedItemsWithDetails.length);
              }
              
              // Mark sync as complete so useEffect can fetch updated cart
              setIsCartSyncInProgress(false);
            } catch (syncError) {
              // sendRequest throws error.response || error.message
              // So syncError might be the response object directly
              const errorResponse = syncError?.response || syncError;
              const errorData = errorResponse?.data || syncError?.data || syncError;
              const errorStatus = errorResponse?.status || syncError?.status;
              
              console.error("Error syncing guest cart (signup):", syncError);
              console.error("Sync error response (signup):", errorResponse);
              console.error("Sync error data (signup):", errorData);
              console.error("Sync error status (signup):", errorStatus);
              console.error("Full error object (signup):", syncError);
              
              // If it's a 401, the token might be invalid - try using authToken from localStorage
              if (errorStatus === 401) {
                console.warn("401 Unauthorized (signup) - token might be invalid, trying with localStorage token");
                const storedToken = localStorage.getItem("authToken");
                if (storedToken && storedToken !== response.data.token) {
                  console.log("Retrying with stored token (signup)");
                  try {
                    const retryResponse = await sendRequest(
                      "POST",
                      `${BASE_URL}/v1/public_site/customers/${response.data.customer.id}/cart/sync_guest_cart`,
                      { items: itemsToSync },
                      { Authorization: storedToken }
                    );
                    console.log("Retry successful (signup):", retryResponse);
                    localStorage.removeItem("guestCart");
                    window.dispatchEvent(new Event('cart-updated'));
                  } catch (retryError) {
                    console.error("Retry also failed (signup):", retryError);
                  }
                }
              }
              
              // Don't clear guest cart if sync fails
              // Mark sync as complete even on error so cart can be fetched
              setIsCartSyncInProgress(false);
            }
          } else {
            console.warn("No valid items to sync from guest cart (signup)");
            console.warn("Guest cart items were (signup):", guestCartItems);
            // Mark sync as complete
            setIsCartSyncInProgress(false);
          }
        } else {
          console.log("No guest cart items or customer ID/token missing (signup)");
          // Mark sync as complete
          setIsCartSyncInProgress(false);
        }
        
        // Close modal properly
        const modalElement = document.getElementById("signupModal");
        if (modalElement && window.bootstrap) {
          const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
          if (modalInstance) {
            modalInstance.hide();
          }
        }
        closeSignupModal();
        
        // Force update login state immediately for pet_products business
        // This ensures checkout button works right after signup
        if (businessCategory === "pet_products") {
          // Force re-check login state
          setLoginStateKey(prev => prev + 1);
          // Wait a bit for state to update, then proceed
          setTimeout(() => {
            // If on cart page, don't navigate - let user proceed with checkout
            // Otherwise navigate to customer-info
            if (window.location.pathname === '/cart') {
              // Stay on cart page, user can click checkout again
              console.log('User signed up on cart page - ready for checkout');
            } else {
              navigate('/customer-info');
            }
          }, 300);
        } else {
          // After successful signup, navigate directly to customer-info
          // Cart is already synced, so we can proceed to checkout
          setTimeout(() => {
            navigate('/customer-info');
          }, 300);
        }
      }
    } catch (error) {
      console.error("Error signing up:", error);
      // Reset sync flag on error
      setIsCartSyncInProgress(false);
    }
  };

  if (loading) return <div className="container my-5 py-3 text-center">Loading cart items...</div>;

  if (error) return (
    <div className="container my-5 py-3 text-center">
      <h2>Error</h2>
      <p>{error}</p>
    </div>
  );

  return (
    <>
      {cartItems.length === 0 ? (
        <div className="empty-cart-container">
          <div className="empty-cart-content">
            <FaShoppingCart className="empty-cart-icon" />
            <h2>Your Cart is Empty</h2>
            <p>Looks like you haven't added any items to your cart yet.</p>
            <button 
              className="btn-continue-shopping"
              onClick={() => window.history.back()}
            >
              Continue Shopping
            </button>
          </div>
        </div>
      ) : (
        <div className="cart-container">
          <div className="cart-header">
            <div className="cart-header-top">
              <div>
                <h1>Your Shopping Cart</h1>
                <p className="cart-subtitle">
                  {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'} in your cart
                </p>
              </div>
              <div className="select-all-controls">
                {selectedItems.size === cartItems.length && cartItems.length > 0 ? (
                  <button 
                    className="select-all-btn"
                    onClick={handleDeselectAll}
                  >
                    Deselect All
                  </button>
                ) : (
                  <button 
                    className="select-all-btn"
                    onClick={handleSelectAll}
                  >
                    Select All
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="cart-content">
            <div className="cart-items-section">
              {cartItems.map((cartItem, index) => {
                const itemId = getItemId(cartItem);
                const uniqueKey = `${itemId}_${index}`;
                const isSelected = selectedItems.has(itemId);
                return (
                  <div className="cart-item-card" key={uniqueKey}>
                    <div className="cart-item-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleItemSelection(itemId)}
                        id={`cart-item-${itemId}`}
                      />
                      <label htmlFor={`cart-item-${itemId}`} className="sr-only">
                        Select {cartItem.item_name}
                      </label>
                    </div>
                    <div className="cart-item-image">
                      <img 
                        src={cartItem.img || defaultImg} 
                        alt={cartItem.item_name} 
                        onError={(e) => e.target.src = defaultImg}
                      />
                    </div>
                    
                    <div className="cart-item-details">
                      <div className="cart-item-header">
                        <h3 className="cart-item-name">{cartItem.item_name}</h3>
                        <button 
                          className="remove-item-btn"
                          onClick={() => handleRemoveItem(cartItem)}
                          title="Remove from cart"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    
                    <div className="cart-item-price">
                      <span className="price-amount">₹{calculateItemTotal(cartItem).toFixed(2)}</span>
                      <span className="price-per-unit">(₹{calculateFinalPrice(cartItem.sales_price, cartItem.sales_discount)} each)</span>
                    </div>
                    
                    <div className="cart-item-controls">
                      <div className="quantity-controls">
                        <button 
                          className="quantity-btn"
                          onClick={() => handleQuantityChange(cartItem, -1)}
                          disabled={cartItem.quantity <= 1}
                        >
                          <FaMinus />
                        </button>
                        <span className="quantity-display">{cartItem.quantity}</span>
                        <button 
                          className="quantity-btn"
                          onClick={() => handleQuantityChange(cartItem, 1)}
                          disabled={cartItem.available_quantity && cartItem.available_quantity !== -1 && cartItem.quantity >= cartItem.available_quantity}
                        >
                          <FaPlus />
                        </button>
                      </div>
                      
                      {cartItem.available_quantity !== -1 && (
                        <div className="stock-info">
                          <span className={`stock-status ${
                            cartItem.available_quantity === 0 ? 'out-of-stock' : 
                            cartItem.available_quantity <= 5 ? 'low-stock' : 'in-stock'
                          }`}>
                            <FaExclamationTriangle className="stock-icon" />
                            {cartItem.available_quantity || 0} remaining
                            {cartItem.available_quantity <= 5 && cartItem.available_quantity > 0 && (
                              <span className="low-stock-text"> (Low Stock!)</span>
                            )}
                            {cartItem.available_quantity === 0 && (
                              <span className="out-of-stock-text"> (Out of Stock!)</span>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
            
            <div className="cart-summary">
              <div className="summary-card">
                <h3>Order Summary</h3>
                {(() => {
                  // Filter to only selected items
                  const selectedCartItems = cartItems.filter(item => {
                    const itemId = getItemId(item);
                    return selectedItems.has(itemId);
                  });
                  
                  const { hasOutOfStockItems, outOfStockItems } = checkCartStockAvailability(selectedCartItems);
                  
                  return (
                    <>
                      <div className="summary-row">
                        <span>Items ({selectedCartItems.length}):</span>
                        <span>₹{calculateCartTotal(selectedCartItems).toFixed(2)}</span>
                      </div>
                      <div className="summary-row total">
                        <span>Total:</span>
                        <span className="total-amount">₹{calculateCartTotal(selectedCartItems).toFixed(2)}</span>
                      </div>
                      
                      {selectedCartItems.length === 0 ? (
                        <div className="checkout-disabled-container">
                          <button 
                            className="checkout-btn disabled"
                            disabled={true}
                          >
                            <FaShoppingCart className="checkout-icon" />
                            Please Select Items to Checkout
                          </button>
                        </div>
                      ) : hasOutOfStockItems ? (
                        <div className="checkout-disabled-container">
                          <button 
                            className="checkout-btn disabled"
                            disabled={true}
                          >
                            <FaExclamationTriangle className="checkout-icon" />
                            Cannot Checkout - Items Out of Stock
                          </button>
                          <div className="out-of-stock-message">
                            <p>The following items are out of stock:</p>
                            <ul>
                              {outOfStockItems.map((item, index) => (
                                <li key={index}>
                                  {item.item_name} - Only {item.available_quantity || 0} available
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ) : (
                        <button 
                          className="checkout-btn"
                          disabled={isProcessingCheckout || selectedCartItems.length === 0}
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            
                            // Prevent multiple clicks
                            if (isProcessingCheckout || selectedCartItems.length === 0) {
                              return;
                            }
                            
                            setIsProcessingCheckout(true);
                            
                            try {
                              // Check if user is logged in - also check localStorage directly
                              // This is especially important for pet_products business after OTP login
                              const userDetailsFromStorage = JSON.parse(localStorage.getItem("userDetails") || "null");
                              const isActuallyLoggedIn = isLoggedIn || (userDetailsFromStorage && userDetailsFromStorage.data?.customer?.id);
                              
                              if (!isActuallyLoggedIn) {
                                // Reset modal state before opening
                                setActiveTab("login");
                                setShowOtpLogin(false);
                                setPhoneNumber("");
                                setOtp("");
                                setFirstName("");
                                setLastName("");
                                setEmail("");
                                // Set showLoginModal to true to trigger modal
                                setShowLoginModal(true);
                                setIsProcessingCheckout(false);
                                return;
                              }
                              
                              // If logged in, proceed with checkout using only selected items
                              // Double-check that we're only using selected items
                              const finalSelectedItems = cartItems.filter(item => {
                                const itemId = getItemId(item);
                                return selectedItems.has(itemId);
                              });
                              
                              // Ensure all items have item_id (product ID) for API calls
                              // item_id is the actual product ID needed for checkout
                              const itemsWithProductId = finalSelectedItems.map(item => {
                                // Extract product ID from item
                                // Priority: item.item_id (if it's a number) > extract from guest ID > item.id
                                let productId = item.item_id;
                                
                                // If item_id is not a valid number, try to extract from id
                                if (!productId || isNaN(parseInt(productId))) {
                                  // If id is a guest cart ID like 'guest_1766149267542_63050', extract the number at the end
                                  if (typeof item.id === 'string' && item.id.includes('_')) {
                                    const parts = item.id.split('_');
                                    const lastPart = parts[parts.length - 1];
                                    const extractedId = parseInt(lastPart);
                                    if (!isNaN(extractedId)) {
                                      productId = extractedId;
                                    }
                                  } else {
                                    // Try to parse id directly
                                    const parsedId = parseInt(item.id);
                                    if (!isNaN(parsedId)) {
                                      productId = parsedId;
                                    }
                                  }
                                } else {
                                  // Ensure item_id is a number
                                  productId = parseInt(productId);
                                }
                                
                                return {
                                  ...item,
                                  item_id: productId
                                };
                              });
                              
                              console.log('Checkout - Selected items count:', finalSelectedItems.length);
                              console.log('Checkout - Total cart items:', cartItems.length);
                              console.log('Checkout - Selected item IDs:', Array.from(selectedItems));
                              console.log('Checkout - Items with product IDs:', itemsWithProductId.map(i => ({ id: i.id, item_id: i.item_id })));
                              
                              if (finalSelectedItems.length === 0) {
                                showWarning('Please select at least one item to checkout');
                                setIsProcessingCheckout(false);
                                return;
                              }
                              
                              const { handleCheckout } = CheckoutHandler({
                                items: itemsWithProductId,
                                setShowLoginModal: setShowLoginModal,
                                navigate,
                                dispatch,
                                isLoggedIn
                              });
                              await handleCheckout();
                            } catch (error) {
                              console.error('Error during checkout:', error);
                              setIsProcessingCheckout(false);
                            }
                          }}
                        >
                          <FaShoppingCart className="checkout-icon" />
                          {isProcessingCheckout ? 'Processing...' : 'Proceed to Checkout'}
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      <SignupModal
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        showOtpLogin={showOtpLogin}
        setShowOtpLogin={setShowOtpLogin}
        handleSignUp={handleSignUp}
        handleSendOtp={handleSendOtp}
        handleVerifyOtp={handleVerifyOtp}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        otp={otp}
        setOtp={setOtp}
        firstName={firstName}
        setFirstName={setFirstName}
        lastName={lastName}
        setLastName={setLastName}
        email={email}
        setEmail={setEmail}
      />
      
      <MessageModal
        isOpen={modalState.isOpen}
        message={modalState.message}
        type={modalState.type}
        onClose={closeModal}
        autoClose={modalState.autoClose}
        autoCloseDelay={modalState.autoCloseDelay}
        showCloseButton={modalState.showCloseButton}
      />
    </>
  );
};

export default Cart;
